<template>
  <div class="cv-columns">
    <div class="cv-columns__title">
      <span>Columns you can use</span>
      <b-button
        v-if="canEdit"
        v-b-tooltip.hover
        title="Edit column descriptions"
        aria-label="Edit column descriptions"
        size="sm"
        variant="link"
        class="cv-columns__pencil"
        @click="openEditor"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
      </b-button>
    </div>
    <b-form-input
      v-model="search"
      type="search"
      size="sm"
      placeholder="Search names and descriptions"
      aria-label="Search columns"
    />
    <p class="text-muted small mb-1 mt-1">{{ filtered.length }} of {{ columns.length }} columns</p>

    <ul class="cv-columns__list">
      <li v-for="column in filtered" :key="column.name" class="cv-columns__item">
        <div class="cv-columns__head">
          <span class="font-weight-bold">{{ columnLabel(column.name) }}</span>
          <b-badge variant="light">{{ typeLabel(column.data_type) }}</b-badge>
        </div>
        <div class="cv-columns__name">{{ column.name }}</div>
        <div class="cv-columns__description" :class="{ 'text-muted': !column.description }">
          {{ column.description || 'No description yet.' }}
        </div>
      </li>
    </ul>

    <!-- Superusers: every column's description in one place. Shared by every company. -->
    <b-modal
      v-model="editorOpen"
      title="Edit column descriptions"
      size="lg"
      scrollable
      modal-class="cv-builder"
      no-close-on-backdrop
      @hide="onEditorHide"
    >
      <p class="small text-muted">
        These descriptions help everyone who builds a rule, for every company. Change any of them, then
        save.
      </p>
      <b-form-input
        v-model="editSearch"
        type="search"
        size="sm"
        class="mb-2"
        placeholder="Search names and descriptions"
        aria-label="Search columns to edit"
      />
      <b-alert v-if="saveError" show variant="danger" class="small">{{ saveError }}</b-alert>
      <div v-for="column in editFiltered" :key="column.name" class="cv-columns__edit-row">
        <div class="cv-columns__head">
          <label :for="`cv-desc-${column.name}`" class="font-weight-bold mb-0">{{ columnLabel(column.name) }}</label>
          <span class="small text-muted">
            {{ typeLabel(column.data_type) }} ·
            <b-button size="sm" variant="link" class="p-0 align-baseline" @click="openHistory(column)">History</b-button>
          </span>
        </div>
        <div class="cv-columns__name">{{ column.name }}</div>
        <b-form-textarea
          :id="`cv-desc-${column.name}`"
          v-model="drafts[column.name]"
          rows="2"
          max-rows="4"
          maxlength="500"
          size="sm"
          :state="rowErrors[column.name] ? false : null"
          :class="{ 'cv-columns__changed': isChanged(column) }"
        />
        <div v-if="rowErrors[column.name]" class="small text-danger">{{ rowErrors[column.name] }}</div>
      </div>
      <template #modal-footer>
        <span class="small text-muted mr-auto">{{ changedCount }} changed</span>
        <b-button variant="link" :disabled="saving" @click="editorOpen = false">Cancel</b-button>
        <b-button variant="primary" :disabled="saving || !changedCount" @click="saveAll">
          <cv-spinner v-if="saving" class="mr-1" label="Saving" />
          Save changes
        </b-button>
      </template>
    </b-modal>

    <b-modal v-model="historyOpen" :title="historyTitle" size="lg" hide-footer>
      <div v-if="historyLoading" class="py-3 text-center">
        <cv-spinner class="mr-2" />
        Loading history...
      </div>
      <b-alert v-else-if="historyError" show variant="danger">{{ historyError }}</b-alert>
      <p v-else-if="!history.length" class="text-muted mb-0">
        This description hasn't been changed since it was first written.
      </p>
      <ul v-else class="cv-history">
        <li v-for="(event, index) in history" :key="index" class="cv-history__event">
          <div class="text-muted small">{{ formatWhen(event.occurred_at) }} by {{ event.actor }}</div>
          <div class="small">Was: {{ event.before || '(none)' }}</div>
          <div class="small">Now: {{ event.after || '(none)' }}</div>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>
import CustomValidationsService from '@services/customValidationsService'
import { columnLabel } from '@util/criteriaText'
import CvSpinner from './CvSpinner.vue'

const TYPE_LABELS = { text: 'Text', numeric: 'Number', date: 'Date', boolean: 'Yes / No' }

const errorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.detail) || fallback

const matches = (column, search) => {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const haystack = `${column.name} ${columnLabel(column.name)} ${column.description || ''}`.toLowerCase()
  return words.every((word) => haystack.includes(word))
}

// The rule builder's column reference (CV-5, LIQ-2272): every BDX column a rule can use, with its
// type and what it holds - read-only. LimeIQ superusers (canEdit) get one pencil in the header that
// opens every description for editing; they're shared by every company, and each change is in the
// column's history.
export default {
  name: 'RuleColumnsPanel',

  components: { CvSpinner },

  props: {
    // [{ name, data_type, description }] - GET /api/custom-validations/columns
    columns: {
      type: Array,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      search: '',
      editorOpen: false,
      editSearch: '',
      drafts: {},
      rowErrors: {},
      saving: false,
      saveError: null,
      historyOpen: false,
      historyColumn: null,
      historyLoading: false,
      historyError: null,
      history: []
    }
  },

  computed: {
    filtered() {
      return this.columns.filter((column) => matches(column, this.search))
    },
    editFiltered() {
      return this.columns.filter((column) => matches(column, this.editSearch))
    },
    changedCount() {
      return this.columns.filter((column) => this.isChanged(column)).length
    },
    historyTitle() {
      return this.historyColumn ? `History - ${columnLabel(this.historyColumn.name)}` : 'History'
    }
  },

  methods: {
    columnLabel,

    typeLabel(type) {
      return TYPE_LABELS[type] || type
    },

    openEditor() {
      this.drafts = Object.fromEntries(this.columns.map((column) => [column.name, column.description || '']))
      this.rowErrors = {}
      this.saveError = null
      this.editSearch = ''
      this.editorOpen = true
    },

    isChanged(column) {
      const draft = this.drafts[column.name]
      return draft !== undefined && draft.trim() !== (column.description || '')
    },

    // Saves each changed description in turn; one refused doesn't stop the others.
    async saveAll() {
      this.saving = true
      this.saveError = null
      this.rowErrors = {}
      const changed = this.columns.filter((column) => this.isChanged(column))
      for (const column of changed) {
        try {
          const { data } = await CustomValidationsService.setColumnDescription(column.name, this.drafts[column.name].trim())
          this.$emit('updated', data)
        } catch (err) {
          this.$set(this.rowErrors, column.name, errorMessage(err, 'This description could not be saved.'))
        }
      }
      this.saving = false
      const failed = Object.keys(this.rowErrors).length
      if (failed) {
        this.saveError = `${failed} description${failed === 1 ? '' : 's'} could not be saved - see below.`
      } else {
        this.drafts = {}
        this.editorOpen = false
        this.$bvToast.toast(`${changed.length} description${changed.length === 1 ? '' : 's'} saved.`, {
          title: 'Saved',
          variant: 'success',
          solid: true
        })
      }
    },

    // Closing with unsaved edits asks first.
    async onEditorHide(event) {
      if (this.saving) {
        event.preventDefault()
        return
      }
      if (!this.changedCount) return
      event.preventDefault()
      const result = await this.$swal({
        title: 'Discard your changes?',
        text: 'The descriptions you changed haven\'t been saved.',
        showCancelButton: true,
        confirmButtonText: 'DISCARD',
        cancelButtonText: 'KEEP EDITING'
      })
      if (result && result.value) {
        this.drafts = {}
        this.editorOpen = false
      }
    },

    async openHistory(column) {
      this.historyColumn = column
      this.historyOpen = true
      this.historyLoading = true
      this.historyError = null
      this.history = []
      try {
        const { data } = await CustomValidationsService.getColumnHistory(column.name)
        this.history = data.events || []
      } catch (err) {
        this.historyError = errorMessage(err, 'Failed to load the history.')
      } finally {
        this.historyLoading = false
      }
    },

    // The API sends UTC times without a zone.
    formatWhen(value) {
      if (!value) return ''
      const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`)
      return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style>
.cv-columns {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.cv-columns__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-weight: bold;
}

.cv-columns__pencil {
  padding: 2px 6px;
  color: #4353c9;
}

.cv-columns__list {
  flex: 1;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cv-columns__item,
.cv-columns__edit-row {
  padding: 6px 0;
  border-bottom: 1px solid #eef0f2;
  font-size: 0.85rem;
}

.cv-columns__edit-row {
  padding: 10px 0;
}

.cv-columns__head {
  display: flex;
  justify-content: space-between;
  gap: 6px;
}

.cv-columns__name {
  margin-bottom: 4px;
  color: #8492a6;
  font-size: 0.75rem;
}

/* Beats the builder's darker input border, which would otherwise hide which rows changed. */
.cv-builder .form-control.cv-columns__changed:not(.is-invalid) {
  border-color: #c77700;
  background: #fffaf2;
}
</style>
